import * as THREE from 'three';
import type {
    SectionPlaneUpdateInput,
    SectionViewPlaneIndex,
    SectionViewVectorTuple,
} from '../../types/section-view/SectionViewTypes';
import { ReferencePlane } from '../reference/ReferencePlane';

const MILLIMETERS_PER_METER = 1000;
const DEGREES_TO_RADIANS = Math.PI / 180;
const MINIMUM_VECTOR_LENGTH_SQUARED = 1e-12;

/**
 * 瞬态剖切数学平面。
 *
 * 职责与边界：
 * 1. 只负责把参考坐标系、偏移、双向旋转和反向转换成世界空间 `THREE.Plane`；
 * 2. 不进入 DatumPlaneManager，不携带正式基准特征、历史或选择语义；
 * 3. 以负数 ID 调用 ReferencePlane，避免消耗正式基准面的递增 ID。
 * 4. 实例不创建 GPU 资源，生命周期由 SectionViewController 统一持有，无需独立释放。
 */
export class SectionPlane extends ReferencePlane {
    /** 固定剖面槽位。 */
    public readonly sectionIndex: SectionViewPlaneIndex;

    /** 更新计算复用的参考原点。 */
    private readonly referenceOriginBuffer = new THREE.Vector3();
    /**
     * 剖切坐标系原点。
     *
     * THREE.Plane 只保存法向和常量，会丢失平面内的原点分量；“所选基准面”偏移在
     * 旋转后可能包含该分量，因此必须独立保存完整坐标系原点供预览、箭头和局部坐标使用。
     */
    private readonly sectionOriginBuffer = new THREE.Vector3();
    /** 更新计算复用的参考法向。 */
    private readonly referenceNormalBuffer = new THREE.Vector3();
    /** 更新计算复用的当前法向。 */
    private readonly orientedNormalBuffer = new THREE.Vector3();
    /** 更新计算复用的局部 X 轴。 */
    private readonly xAxisBuffer = new THREE.Vector3();
    /** 更新计算复用的局部 Y 轴。 */
    private readonly yAxisBuffer = new THREE.Vector3();
    /** 旋转操纵器绕 X 旋转时使用的稳定世界轴。 */
    private readonly rotationXAxisBuffer = new THREE.Vector3(1, 0, 0);
    /** 旋转操纵器绕 Y 旋转时使用的稳定世界轴。 */
    private readonly rotationYAxisBuffer = new THREE.Vector3(0, 1, 0);
    /** 更新计算复用的偏移方向。 */
    private readonly offsetDirectionBuffer = new THREE.Vector3();
    /** 更新计算复用的旋转四元数。 */
    private readonly rotationBuffer = new THREE.Quaternion();

    /**
     * 创建指定槽位的瞬态剖切面。
     *
     * @param sectionIndex 零基剖面槽位。
     * @throws 槽位值由调用方的 SectionViewPlaneIndex 契约保证；基类初始化失败时原样传播异常。
     */
    public constructor(sectionIndex: SectionViewPlaneIndex) {
        super(
            `剖面 ${sectionIndex + 1}`,
            new THREE.Vector3(0, 0, 1),
            0,
            {},
            null,
            -(sectionIndex + 1),
        );
        this.sectionIndex = sectionIndex;
    }

    /**
     * 获取包含平面内偏移分量的剖切坐标系原点。
     *
     * @returns 与内部缓存隔离的世界坐标原点副本。
     */
    public override get originWorld(): THREE.Vector3 {
        return this.sectionOriginBuffer.clone();
    }

    /**
     * 将包含平面内偏移分量的剖切坐标系原点写入目标向量。
     *
     * @param out 输出向量。
     * @returns 写入后的输出向量。
     */
    public override copyOriginWorldTo(out: THREE.Vector3): THREE.Vector3 {
        return out.copy(this.sectionOriginBuffer);
    }

    /**
     * 将当前 X 旋转手柄使用的世界轴写入目标向量。
     *
     * 该轴是参考局部 X 轴；后续 Y 旋转和反向只改变剖切结果，不得改变 X 手柄语义。
     *
     * @param out 输出向量。
     * @returns 写入后的输出向量。
     */
    public copyRotationXAxisWorldTo(out: THREE.Vector3): THREE.Vector3 {
        return out.copy(this.rotationXAxisBuffer);
    }

    /**
     * 将当前 Y 旋转手柄使用的世界轴写入目标向量。
     *
     * 该轴已应用 X 旋转，严格对应当前属性栏先 X 后 Y 的旋转顺序。
     *
     * @param out 输出向量。
     * @returns 写入后的输出向量。
     */
    public copyRotationYAxisWorldTo(out: THREE.Vector3): THREE.Vector3 {
        return out.copy(this.rotationYAxisBuffer);
    }

    /**
     * 将最终剖面姿态的显示 X 轴写入目标向量。
     *
     * 该轴已完成 X/Y 顺序旋转，只用于统一平移轴与旋转环的最终显示坐标帧；
     * 属性角求值仍由 `copyRotationXAxisWorldTo` 提供稳定轴。
     *
     * @param out 输出向量。
     * @returns 写入后的输出向量。
     */
    public copyDisplayXAxisWorldTo(out: THREE.Vector3): THREE.Vector3 {
        return out.copy(this.xAxisBuffer);
    }

    /**
     * 将最终剖面姿态的显示 Y 轴写入目标向量。
     *
     * @param out 输出向量。
     * @returns 写入后的输出向量。
     */
    public copyDisplayYAxisWorldTo(out: THREE.Vector3): THREE.Vector3 {
        return out.copy(this.yAxisBuffer);
    }

    /**
     * 将与平移箭头一致的最终剖面法向写入目标向量。
     *
     * 反向选项会翻转该法向，但不会改变 X/Y 属性角的参数求值轴。
     *
     * @param out 输出向量。
     * @returns 写入后的输出向量。
     */
    public copyDisplayNormalWorldTo(out: THREE.Vector3): THREE.Vector3 {
        return out.copy(this.orientedNormalBuffer);
    }

    /**
     * 根据 PropertyManager 定义更新世界空间剖切平面。
     *
     * @param input 已校验的剖切定义。
     * @returns 无返回值。
     * @throws 当参考坐标系或数值无效时抛出明确错误，禁止无效平面进入渲染链路。
     */
    public updateFromDefinition(input: SectionPlaneUpdateInput): void {
        this.assertFiniteNumber(input.offsetMillimeters, 'offsetMillimeters');
        this.assertFiniteNumber(input.rotationXDegrees, 'rotationXDegrees');
        this.assertFiniteNumber(input.rotationYDegrees, 'rotationYDegrees');

        this.setVectorFromTuple(this.referenceOriginBuffer, input.reference.origin, 'reference.origin');
        this.setVectorFromTuple(this.referenceNormalBuffer, input.reference.normal, 'reference.normal');
        this.setVectorFromTuple(this.xAxisBuffer, input.reference.xAxis, 'reference.xAxis');
        this.setVectorFromTuple(this.yAxisBuffer, input.reference.yAxis, 'reference.yAxis');

        if (this.referenceNormalBuffer.lengthSq() <= MINIMUM_VECTOR_LENGTH_SQUARED) {
            throw new Error('剖面视图参考法向不能为零向量: reference.normal');
        }
        this.referenceNormalBuffer.normalize();

        // 参考轴先投影到参考面，再由法向重建右手系；持久化中的轻微浮点漂移不会逐次保存放大。
        this.xAxisBuffer.addScaledVector(
            this.referenceNormalBuffer,
            -this.xAxisBuffer.dot(this.referenceNormalBuffer),
        );
        if (this.xAxisBuffer.lengthSq() <= MINIMUM_VECTOR_LENGTH_SQUARED) {
            throw new Error('剖面视图参考 X 轴与法向平行: reference.xAxis');
        }
        this.xAxisBuffer.normalize();
        this.orientedNormalBuffer.copy(this.referenceNormalBuffer);
        this.yAxisBuffer.crossVectors(this.orientedNormalBuffer, this.xAxisBuffer).normalize();

        // 若输入 Y 轴表达了相反朝向，则同步翻转 X/Y，保留持久化坐标系的方向。
        this.offsetDirectionBuffer.set(
            input.reference.yAxis[0],
            input.reference.yAxis[1],
            input.reference.yAxis[2],
        );
        if (this.yAxisBuffer.dot(this.offsetDirectionBuffer) < 0) {
            this.xAxisBuffer.negate();
            this.yAxisBuffer.negate();
        }

        // SOLIDWORKS 的 X/Y 角度基于参考面局部轴依次定向，而不是世界欧拉角。
        this.rotationXAxisBuffer.copy(this.xAxisBuffer);
        this.rotationBuffer.setFromAxisAngle(
            this.xAxisBuffer,
            input.rotationXDegrees * DEGREES_TO_RADIANS,
        );
        this.yAxisBuffer.applyQuaternion(this.rotationBuffer).normalize();
        this.orientedNormalBuffer.applyQuaternion(this.rotationBuffer).normalize();
        this.rotationYAxisBuffer.copy(this.yAxisBuffer);

        this.rotationBuffer.setFromAxisAngle(
            this.yAxisBuffer,
            input.rotationYDegrees * DEGREES_TO_RADIANS,
        );
        this.xAxisBuffer.applyQuaternion(this.rotationBuffer).normalize();
        this.orientedNormalBuffer.applyQuaternion(this.rotationBuffer).normalize();

        if (input.offsetMethod === 'reference-plane') {
            this.offsetDirectionBuffer.copy(this.orientedNormalBuffer);
        } else {
            this.setVectorFromTuple(
                this.offsetDirectionBuffer,
                input.selectedPlaneNormal,
                'selectedPlaneNormal',
            );
            if (this.offsetDirectionBuffer.lengthSq() <= MINIMUM_VECTOR_LENGTH_SQUARED) {
                throw new Error('剖面视图所选基准面偏移法向不能为零向量: selectedPlaneNormal');
            }
            this.offsetDirectionBuffer.normalize();
        }

        this.referenceOriginBuffer.addScaledVector(
            this.offsetDirectionBuffer,
            input.offsetMillimeters / MILLIMETERS_PER_METER,
        );
        this.sectionOriginBuffer.copy(this.referenceOriginBuffer);

        if (input.reverse) {
            this.orientedNormalBuffer.negate();
        }

        this.updateReferenceFrame(
            this.referenceOriginBuffer,
            this.orientedNormalBuffer,
            {
                xAxis: this.xAxisBuffer,
                yAxis: this.yAxisBuffer,
            },
        );
    }

    /**
     * 把可序列化三元组写入复用向量并校验有限数。
     *
     * @param target 目标向量。
     * @param tuple 输入三元组。
     * @param fieldName 错误信息中的字段名。
     * @returns 无返回值。
     * @throws 三元组长度不正确或包含非有限数时抛出错误。
     */
    private setVectorFromTuple(
        target: THREE.Vector3,
        tuple: SectionViewVectorTuple,
        fieldName: string,
    ): void {
        if (tuple.length !== 3) {
            throw new Error(`剖面视图三维向量必须包含三个分量: ${fieldName}`);
        }
        tuple.forEach((component, index) => this.assertFiniteNumber(component, `${fieldName}[${index}]`));
        target.set(tuple[0], tuple[1], tuple[2]);
    }

    /**
     * 校验渲染数学输入为有限数字。
     *
     * @param value 待校验值。
     * @param fieldName 错误信息中的字段名。
     * @returns 无返回值。
     * @throws 待校验值不是有限数时抛出错误。
     */
    private assertFiniteNumber(value: number, fieldName: string): void {
        if (!Number.isFinite(value)) {
            throw new Error(`剖面视图字段必须是有限数字: ${fieldName}`);
        }
    }
}
